import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Briefcase, Heart } from 'lucide-react';

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 text-white overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-yellow-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-yellow-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="text-center lg:text-left animate-slideInLeft">
              <h1 data-testid="hero-title" className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Fernando Meslin
                <span className="block text-yellow-400 mt-2">de Farias</span>
              </h1>
              <p data-testid="hero-subtitle" className="text-xl md:text-2xl mb-8 text-gray-200">
                Profissional Administrativo e Financeiro
              </p>
              <p className="text-lg mb-8 text-gray-300">
                Excelência em gestão administrativa, controle fiscal e rotinas financeiras,
                com compromisso, responsabilidade e valores cristãos.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a
                  href="https://wa.me/5521991508223?text=Olá%20Fernando!%20Gostaria%20de%20entrar%20em%20contato."
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-whatsapp-button"
                  className="inline-flex items-center justify-center px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-full transition-all duration-300 hover:scale-105 shadow-xl"
                >
                  Entre em Contato
                  <ArrowRight className="ml-2" size={20} />
                </a>
                <Link
                  to="/curriculo"
                  data-testid="hero-resume-button"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-full transition-all duration-300 border-2 border-white/30"
                >
                  Ver Currículo
                </Link>
              </div>
            </div>

            {/* Profile Image */}
            <div className="flex justify-center lg:justify-end animate-slideInRight">
              <div className="relative">
                <div className="absolute inset-0 bg-yellow-400 rounded-full blur-3xl opacity-30"></div>
                <img
                  src="https://customer-assets.emergentagent.com/job_meslin-dev/artifacts/gbyh8spl_1000045144.png"
                  alt="Fernando Meslin"
                  data-testid="hero-image"
                  className="relative w-80 h-80 object-cover rounded-full border-8 border-yellow-400 shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-blue-900 mb-4">Por Que Me Escolher?</h2>
            <div className="w-24 h-1 bg-yellow-400 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div data-testid="feature-experience" className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <Briefcase className="text-blue-900" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-blue-900 mb-4">Experiência Sólida</h3>
              <p className="text-gray-600">
                Mais de 20 anos de atuação em rotinas administrativas, financeiras e fiscais,
                com passagens por empresas de diversos segmentos.
              </p>
            </div>

            {/* Feature 2 */}
            <div data-testid="feature-skills" className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6">
                <Award className="text-yellow-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-blue-900 mb-4">Competências Diversas</h3>
              <p className="text-gray-600">
                Especialista em controle fiscal, retenções tributárias, contas a pagar,
                conciliação bancária e relatórios gerenciais.
              </p>
            </div>

            {/* Feature 3 */}
            <div data-testid="feature-values" className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <Heart className="text-red-500" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-blue-900 mb-4">Valores Cristãos</h3>
              <p className="text-gray-600">
                Profissional íntegro, comprometido e responsável, guiado por princípios
                cristãos em todas as áreas da vida.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Vamos Conversar?</h2>
          <p className="text-xl mb-8 text-gray-200">
            Estou disponível para novas oportunidades e projetos.
            Entre em contato e vamos construir algo incrível juntos!
          </p>
          <Link
            to="/contato"
            data-testid="cta-contact-button"
            className="inline-flex items-center justify-center px-8 py-4 bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold rounded-full transition-all duration-300 hover:scale-105 shadow-xl"
          >
            Entrar em Contato
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;